import React from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import SearchTrigger from './SearchTrigger';

interface ResourcesPageHeaderProps {
    title: string;
    searchPlaceholder?: string;
    /** Off when the page places its own search field further down the layout. */
    showSearch?: boolean;
    /** Right-hand side of the toolbar row: New buttons, view toggles, filters. */
    actions?: React.ReactNode;
}

/**
 * The header every resources page carries, built to the app's page pattern
 * rather than its own: title in `text-2xl font-bold text-gray-800` at
 * `px-8 pt-8`, then a toolbar row with the search field on the left and the
 * page's actions on the right — the same shape as the patients and billing
 * pages.
 *
 * `reserveTopRight` pads for the host's overlaid avatar. Only the title row is
 * padded: the toolbar row sits below the avatar, so padding the whole header
 * would pull the page's actions out of line with the content underneath.
 */
export const ResourcesPageHeader: React.FC<ResourcesPageHeaderProps> = ({
    title,
    searchPlaceholder = 'Search resources…',
    showSearch = true,
    actions
}) => {
    const { reserveTopRight } = useResourcesConfig();

    return (
        <div className="flex-shrink-0 px-8 pt-8">
            <h1 className={`text-2xl font-bold text-gray-800 ${reserveTopRight ? 'pr-16' : ''}`}>
                {title}
            </h1>

            {(showSearch || actions) && (
                <div className="flex items-center justify-between gap-4 mt-5">
                    {showSearch && <SearchTrigger placeholder={searchPlaceholder} />}
                    {actions && <div className="ml-auto flex items-center gap-3">{actions}</div>}
                </div>
            )}
        </div>
    );
};

export default ResourcesPageHeader;
